import React, { useContext, useEffect, useState } from "react";
import { Typography, Tooltip, Box, Select, Option, Button, Stack } from "@mui/joy";
import { AlertContext } from "../context/AlertFlag.js";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Info } from "lucide-react";
import { convertTimeString } from "../utils/TimeConversion.js";
import NavBlocker from "../components/modals/NavBlocker.js";
import { NoteTypeSelect } from "../components/custom/NoteTypeSelect.js";
import { get_user_preference, put_user_preferences } from "../services/UserPreferences.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

function SettingsView() {
    const { addAlert } = useContext(AlertContext);
    const { data: preferences, isLoading, error} = useQuery({
        queryKey: ['preferences'],
        queryFn: () => get_user_preference(),
    })
    const queryClient = useQueryClient();
    const mutate_put_user_preference = useMutation({
        mutationFn: put_user_preferences,
        onSuccess: () => {
            addAlert("Saved settings!", "success");
            queryClient.invalidateQueries({ queryKey: ['preferences'] })
        }
    })
    const [ splitHPI, setSplitHPI ] = useState(true);
    const [ noteFormat, setNoteFormat] = useState('');
    const [ noteType, setNoteType ] = useState('');
    const [ noteViews, setNoteViews ] = useState([]);
    const [ updatedAt, setUpdatedAt] = useState('');
    const [ voiceLanguage, setVoiceLanguage ] = useState('en-US');
    const [ noteLanguage, setNoteLanguage ] = useState('en-US');

    const [ dirty, setDirty] = useState(false);

    const handleNoteType = (event, newValue) => {
        setNoteType(newValue);
    }

    const handleVoiceLanguage = (event, voiceInput) => {
        setVoiceLanguage(voiceInput);
    }

    const handleNoteLanguage = (event, noteLanguage) => {
        setNoteLanguage(noteLanguage);
    }

    const handleNoteFormat = (event, newValue) => {
        setNoteFormat(newValue);
    }

    const handleNoteViews = (event, newViews) => {
        setNoteViews(newViews)
    }

    useEffect(() => {
        if (preferences) {
            setSplitHPI(preferences['split_hpi']);
            setNoteFormat(preferences['note_format']);
            setNoteType(preferences['note_type']);
            setNoteViews(preferences['note_views']);
            setVoiceLanguage(preferences['input_language']);
            setNoteLanguage(preferences['output_language']);
            setUpdatedAt(convertTimeString(preferences['updated_at']));
        }
    }, [preferences])

    useEffect(() => {
        if (preferences && (splitHPI !== preferences['split_hpi'] || noteFormat !== preferences['note_format'] || noteType !== preferences['note_type'] ||  voiceLanguage !== preferences['input_language'] || noteLanguage !== preferences['output_language'])) { 
            setDirty(true);
        } else {
            setDirty(false);
        }
    }, [noteFormat, splitHPI, noteType, voiceLanguage, noteLanguage, noteType, preferences]);

    const handleSavePreferences = () => {
        if (noteViews.length <= 0) {
            addAlert("You need to select at least one note view!", "danger");
            return;
        }
        if (!noteType) {
            addAlert("You need to select a default note type!", "danger");
            return;
        }
        try {
            mutate_put_user_preference.mutate({
                split_hpi: splitHPI,
                note_format: noteFormat,
                note_type: noteType,
                note_views: noteViews,
                input_language: voiceLanguage,
                output_language: noteLanguage,
            });
            addAlert("Saved!", "success");
        } catch (error) {
            addAlert(error.message, "danger");
        }
    }

    return (
        <Box sx={{ mt: '25px', ml: '50px', height: '95vh'}}>
            <Box id="outline-it" sx={{
                display: "flex", flexDirection: "column", gap: 2, border: "1px solid #ccc"
            }}>
                <NavBlocker dirty={dirty} />
                <Typography level='h2'>Settings</Typography>
                <Stack sx={{ gap: 1 }}>
                    <Typography level="title-md"> Note Type: </Typography>
                    <NoteTypeSelect value={noteType}
                        disabled={false}
                        onChange={handleNoteType}
                        sx={{
                            width: "100%",
                            minWidth: { sm: "150px", md: "250px"}
                        }}
                        calmTitle={false}
                        noteViews={noteViews}
                        />
                    <Typography level="body-xs">
                        Set your default medical note type.
                        This note type will automatically be selected when you start a new visit. <br/>
                        You'll only see note types from the note views you've configured — to access more, add note views tied to specific specialties and save your changes! <br />
                        Didn't find anything you like? Reach out to our team and we'll set you up with a note type, custom to your preferences.
                    </Typography>
                    <Typography level="title-md">Note Views:</Typography>
                    <Select
                        value={noteViews}
                        multiple
                        onChange={handleNoteViews}
                        sx={{
                            minWidth: "250px",
                        }}
                    >
                        <Option value="pediatric">Pediatric</Option>
                        <Option value="therapy">Therapy</Option>
                        <Option value="radiology">Radiology</Option>
                        <Option value="emergency_response">Emergency Response</Option>
                        <Option value="rehab">Rehab</Option>
                        <Option value="sports_medicine">Sports Medicine</Option>
                        <Option value="physio">Physio</Option>
                        <Option value="general_notes">General Notes</Option>
                        <Option value="athletic">Athletic</Option>
                        <Option value="pedorthist">Pedorthist</Option>
                        <Option value="geriatric">Geriatric</Option>
                        <Option value="work_comp"> Work Comp </Option>
                        <Option value="dental_notes"> Dental Notes </Option>
                        <Option value="wound_care"> Wound Care </Option>
                        <Option value="surgical_notes"> Surgical Notes </Option>
                        <Option value="optometry"> Optometry </Option>
                        <Option value="nursing"> Nursing </Option>
                        <Option value="nephrology"> Nephrology</Option>
                        <Option value="external_letters"> External Letters </Option>
                        <Option value="podiatry"> Podiatry </Option>
                        <Option value="cardiology"> Cardiology </Option>
                        <Option value="office_and_admin"> Office / Staff </Option>
                        <Option value="orthopedic"> Orthopedic </Option>
                        <Option value="occupational_therapy"> Occupational Therapy</Option>
                        <Option value="clinical_psychology"> Clinical Psychology </Option>
                        <Option value="speech_language_pathology"> Speech Language Pathology </Option>
                        <Option value="oncology"> Oncology </Option>
                        {process.env.NODE_ENV === "development" && <Option value="test"> Test </Option>}
                    </Select>
                    <Typography level="body-xs">
                        Customize your note views. <br/>
                        Choose which types of notes you want to see — select the ones that fit your practice best!
                    </Typography>
                    <Typography level="title-md">Voice Language:</Typography>
                    <Select value={voiceLanguage} onChange={handleVoiceLanguage}>
                        <Option value="ar">Arabic</Option>
                        <Option value="nl">Dutch</Option>
                        <Option value="zh-CN"> Chinese(Mandarin, Simplified) </Option>
                        <Option value="zh-TW"> Chinese(Mandarin, Traditional) </Option>
                        <Option value="zh-HK"> Chinese(Cantonese, Simplified) </Option>
                        <Option value="en">English</Option>
                        <Option value="en-US">English(USA)</Option>
                        <Option value="fr">French</Option>
                        <Option value="fr-CA">French(Québecois)</Option>
                        <Option value="de">German</Option>
                        <Option value="ja">Japanese</Option>
                        <Option value="ko">Korean</Option>
                        <Option value="multi" sx={{ width: "100%"}}> 
                            <Tooltip title="Note that Multilanguage only supports Spanish and English." size="sm">
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexGrow: "1"}}>
                                    <Typography> Multilanguage </Typography>
                                    <Info />
                                </Box>
                            </Tooltip>
                        </Option>
                        <Option value="pl">Polish</Option>
                        <Option value="ru">Russian</Option>
                        <Option value="es">Spanish</Option>
                    </Select>
                    <Typography level="body-xs">
                        Specify what language you use while talking to your patients.
                    </Typography>
                    <Typography level="title-md">Note Language: </Typography>
                    <Select value={noteLanguage} onChange={handleNoteLanguage}>
                        <Option value="ar">Arabic</Option>
                        <Option value="nl">Dutch</Option>
                        <Option value="zh-CN"> Chinese(Mandarin, Simplified) </Option>
                        <Option value="zh-TW"> Chinese(Mandarin, Traditional) </Option>
                        <Option value="zh-HK"> Chinese(Cantonese, Simplified) </Option>
                        <Option value="en">English</Option>
                        <Option value="en-US">English(USA)</Option>
                        <Option value="fr">French</Option>
                        <Option value="fr-CA">French(Québecois)</Option>
                        <Option value="de">German</Option>
                        <Option value="ja">Japanese</Option>
                        <Option value="ko">Korean</Option>
                        <Option value="pl">Polish</Option>
                        <Option value="ru">Russian</Option>
                        <Option value="es">Spanish</Option>
                    </Select>
                    <Typography level="body-xs">
                        Speicify which language you would like your medical note to be in.
                    </Typography>
                    <Box sx={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                    }}>
                        <Typography>
                            Last updated at: {updatedAt}
                        </Typography>
                    </Box>
                </Stack>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'start',
                        alignItems: 'start',
                        gap: 2,
                        px: "1rem"
                    }}
                >
                    
                    <Box sx={{ display: 'flex', width: '100%', justifyContent: { xs: 'center', md: 'start' }, }}>
                        <Button onClick={handleSavePreferences} size='lg' sx={{width: '10rem' }}>Save</Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default SettingsView;